import { useEffect, useState } from "react";
import { phaseAuthHeaders, type SessionUser } from "./auth-client";
import { ThemeToggle } from "./App";

type FeedbackItem = {
  id: string;
  userId: string;
  contextType: string;
  contextRef: string | null;
  observationType: string;
  rilevanza: "bassa" | "media" | "alta";
  content: string;
  resolved: boolean;
  createdAt: string;
  user?: { email?: string; name?: string | null };
};

const RIL_COLOR: Record<string, string> = {
  alta: "bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-950/40 dark:text-rose-200 dark:border-rose-800",
  media: "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800",
  bassa: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800/50 dark:text-slate-300 dark:border-slate-700",
};

export function AdminBoardPage({ user }: { user: SessionUser }) {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "open" | "resolved">("open");

  async function load() {
    setLoading(true); setError(null);
    try {
      const r = await fetch("/api/admin/feedback", {
        credentials: "include",
        headers: phaseAuthHeaders(),
      });
      if (r.status === 403) { setError("Non hai i permessi di amministrazione (role=admin richiesto)."); setItems([]); return; }
      if (!r.ok) { setError(`HTTP ${r.status}`); return; }
      const data = (await r.json()) as { items: FeedbackItem[] };
      setItems(data.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function toggleResolved(id: string, current: boolean) {
    await fetch(`/api/admin/feedback/${id}`, {
      method: "PATCH",
      headers: { "content-type": "application/json", ...phaseAuthHeaders() },
      credentials: "include",
      body: JSON.stringify({ resolved: !current }),
    });
    load();
  }

  async function deleteFeedback(id: string) {
    const ok = window.confirm("Cancellare definitivamente questa nota dalla bacheca?");
    if (!ok) return;
    await fetch(`/api/admin/feedback/${id}`, {
      method: "DELETE",
      headers: phaseAuthHeaders(),
      credentials: "include",
    });
    load();
  }

  function exportYaml() {
    const visible = items.filter((i) => filter === "all" || (filter === "open" ? !i.resolved : i.resolved));
    const lines: string[] = ["notes_from_experts:"];
    for (const it of visible) {
      lines.push(`  - id: ${it.id}`);
      lines.push(`    from: ${it.user?.email ?? it.userId}`);
      lines.push(`    context: ${it.contextType}${it.contextRef ? ` / ${it.contextRef}` : ""}`);
      lines.push(`    type: ${it.observationType}`);
      lines.push(`    rilevanza: ${it.rilevanza}`);
      lines.push(`    created: ${it.createdAt}`);
      lines.push(`    resolved: ${it.resolved}`);
      lines.push(`    content: |`);
      for (const line of it.content.split("\n")) {
        lines.push(`      ${line}`);
      }
    }
    const blob = new Blob([lines.join("\n")], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `prora-expert-feedback-${new Date().toISOString().slice(0, 10)}.yaml`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const visible = items.filter((i) => filter === "all" || (filter === "open" ? !i.resolved : i.resolved));

  return (
    <div className="min-h-screen px-6 py-10 bg-slate-50 dark:bg-slate-950 transition-colors">
      <div className="max-w-5xl mx-auto space-y-6">
        <header className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">PRORA — Bacheca feedback esperti</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Vista privata per Lorenzo / Graziano. Utente attivo: {user?.email}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-slate-600 dark:text-slate-400 underline">← home</a>
            <ThemeToggle />
          </div>
        </header>

        <div className="flex items-center gap-3 text-sm">
          <span className="text-slate-700 dark:text-slate-300">Filtro:</span>
          {(["open", "resolved", "all"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded transition-colors ${
                filter === f
                  ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                  : "bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300"
              }`}
            >
              {f === "open" ? "aperti" : f === "resolved" ? "chiusi" : "tutti"}
            </button>
          ))}
          <span className="ml-auto text-slate-500 dark:text-slate-500">{visible.length} elementi</span>
          <button
            onClick={exportYaml}
            disabled={visible.length === 0}
            className="px-3 py-1 border border-slate-300 dark:border-slate-700 rounded bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 text-sm disabled:opacity-50 transition-colors"
          >
            📥 export YAML
          </button>
        </div>

        {loading && <div className="text-sm text-slate-500 dark:text-slate-500">Caricamento…</div>}
        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded p-3">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {visible.length === 0 && !loading && (
            <div className="text-sm text-slate-500 dark:text-slate-500">Nessun feedback in questa vista.</div>
          )}
          {visible.map((it) => (
            <div key={it.id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg p-4 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="text-xs text-slate-500 dark:text-slate-500">
                  <span className="text-slate-700 dark:text-slate-300 font-medium">{it.user?.email ?? it.userId}</span>
                  {" · "}{new Date(it.createdAt).toLocaleString("it-IT")}
                  {" · "}<code className="text-slate-700 dark:text-slate-300">{it.contextType}</code>
                  {it.contextRef && <> · <code className="text-slate-700 dark:text-slate-300">{it.contextRef}</code></>}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded border ${RIL_COLOR[it.rilevanza]}`}>
                  {it.rilevanza}
                </span>
              </div>
              <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">tipo: {it.observationType}</div>
              <div className="text-sm text-slate-900 dark:text-slate-100 mt-3 whitespace-pre-wrap">{it.content}</div>
              <div className="mt-3 flex justify-end gap-2">
                <button
                  onClick={() => deleteFeedback(it.id)}
                  className="text-xs px-3 py-1 border border-rose-300 dark:border-rose-800 rounded text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                >
                  elimina
                </button>
                <button
                  onClick={() => toggleResolved(it.id, it.resolved)}
                  className="text-xs px-3 py-1 border border-slate-300 dark:border-slate-700 rounded text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  {it.resolved ? "↩ riapri" : "✓ marca come incorporato"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
